import { useState } from 'react'
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Stack,
  Card,
  CardContent,
  Grid
} from '@mui/material'
import {
  ExpandMore,
  HelpOutline,
  People,
  Business,
  Payment,
  Security
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'

interface FAQ {
  question: string
  answer: string
  category: 'general' | 'pricing' | 'features' | 'security'
}

const faqs: FAQ[] = [
  // General Questions
  {
    category: 'general',
    question: 'What is this helpdesk system?',
    answer: 'Our helpdesk system is a multi-tenant support platform designed for MSPs and organisations managing multiple customer sites. It allows you to provide white-label support to your customers while maintaining complete control and visibility across all your sites from a single dashboard.'
  },
  {
    category: 'general',
    question: 'Who is this platform designed for?',
    answer: 'This platform is perfect for Managed Service Providers (MSPs), IT support companies, and organisations that manage support for multiple customer locations or departments. Whether you support libraries, retail chains, or corporate clients with multiple sites, our hierarchical structure keeps everything organised.'
  },
  {
    category: 'general',
    question: 'How does the multi-site structure work?',
    answer: 'Our system uses a three-tier hierarchy: Your Organisation (tenant) → Customer Organisations → Child Sites. For example, if you support "City Libraries", they might have child sites like "Central Library", "East Branch", etc. You see all tickets across all sites, while each site only sees their own tickets.'
  },
  {
    category: 'general',
    question: 'Can my customers create their own tickets?',
    answer: 'Yes! Each customer organisation and child site gets their own branded portal where end-users can create tickets, track progress, add comments, and upload attachments. They can access tickets via the web portal or email.'
  },

  // Pricing Questions
  {
    category: 'pricing',
    question: 'How much does it cost?',
    answer: 'Simple, transparent pricing: £25 per agent per month. An "agent" is anyone on your team who responds to and manages tickets. There are no setup fees, no minimums, and no hidden costs. You can start with just 1 agent and scale up as you grow.'
  },
  {
    category: 'pricing',
    question: 'Is there a free trial?',
    answer: 'Yes! Every new account gets a 14-day free trial with full access to all features. No credit card required to start. You can invite your team, set up your customers, and test everything before committing.'
  },
  {
    category: 'pricing',
    question: 'What happens after the trial ends?',
    answer: 'Before your trial ends, you\'ll receive email reminders. To continue using the service, simply upgrade to a paid plan by selecting the number of agent seats you need. All your data remains safe - nothing is deleted.'
  },
  {
    category: 'pricing',
    question: 'Can I change the number of agents later?',
    answer: 'Absolutely! You can increase or decrease your agent count at any time through the billing settings. Changes take effect immediately, and billing is prorated.'
  },
  {
    category: 'pricing',
    question: 'What payment methods do you accept?',
    answer: 'We accept all major credit and debit cards (Visa, Mastercard, American Express) through our secure Stripe payment processing. Billing is monthly and automatic.'
  },

  // Features Questions
  {
    category: 'features',
    question: 'What features are included?',
    answer: 'All plans include: Unlimited tickets, unlimited customer organisations and sites, email ticketing, web portal access, file attachments, internal notes, SLA tracking, reporting and analytics, Microsoft Teams integration, Zapier integration, custom branding, and priority email support.'
  },
  {
    category: 'features',
    question: 'Can I manage multiple customer organisations?',
    answer: 'Yes! There\'s no limit to the number of customer organisations or child sites you can create. This makes it perfect for MSPs managing dozens or hundreds of customer locations from one central platform.'
  },
  {
    category: 'features',
    question: 'Does it support email ticketing?',
    answer: 'Yes! Configure a shared mailbox (Microsoft 365) and incoming emails automatically create tickets. Your team can reply to tickets via the portal or email, and customers receive updates in their inbox.'
  },
  {
    category: 'features',
    question: 'Can I integrate with Microsoft Teams?',
    answer: 'Yes! Get real-time notifications in your Microsoft Teams channel when tickets are created, assigned, updated, or resolved. Setup takes just a few minutes using Power Automate.'
  },
  {
    category: 'features',
    question: 'What about reporting and analytics?',
    answer: 'Built-in reporting shows ticket volumes, resolution times, SLA compliance, agent performance, and trends over time. Filter by customer, site, priority, category, or date range to get the insights you need.'
  },
  {
    category: 'features',
    question: 'Can I customise the branding?',
    answer: 'Each customer organisation can have custom branding including their own subdomain/URL. Your company name and branding appear throughout the platform.'
  },

  // Security Questions
  {
    category: 'security',
    question: 'Is my data secure?',
    answer: 'Yes! We use enterprise-grade security including data encryption at rest and in transit, regular backups, secure authentication, role-based access control, and hosting on Microsoft Azure UK data centres. We take security very seriously.'
  },
  {
    category: 'security',
    question: 'Where is data stored?',
    answer: 'All data is stored in Microsoft Azure data centres in the United Kingdom, ensuring compliance with UK and EU data protection regulations (GDPR).'
  },
  {
    category: 'security',
    question: 'Who can see what data?',
    answer: 'The system uses strict role-based access control: Customers only see their own tickets, child sites only see their site\'s tickets, and your agents/admins see tickets based on their assigned permissions. Data is completely isolated between different customer organisations.'
  },
  {
    category: 'security',
    question: 'Can I export my data?',
    answer: 'Yes! You can export tickets, reports, and customer data at any time. If you decide to leave, we can provide a complete data export.'
  }
]

function FAQPage() {
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState<string | false>(false)
  const [selectedCategory, setSelectedCategory] = useState<string>('all')

  const handleChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false)
  }

  const categories = [
    { id: 'all', label: 'All Questions', icon: <HelpOutline /> },
    { id: 'general', label: 'General', icon: <Business /> },
    { id: 'pricing', label: 'Pricing', icon: <Payment /> },
    { id: 'features', label: 'Features', icon: <People /> },
    { id: 'security', label: 'Security', icon: <Security /> }
  ]

  const filteredFaqs = selectedCategory === 'all'
    ? faqs
    : faqs.filter(faq => faq.category === selectedCategory)

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 8 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Frequently Asked Questions
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
            Everything you need to know about our helpdesk platform
          </Typography>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/')}
          >
            Back to Home
          </Button>
        </Box>

        {/* Category Filter */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {categories.map((category) => (
            <Grid item xs={6} sm={6} md={2.4} key={category.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  bgcolor: selectedCategory === category.id ? 'primary.main' : 'background.paper',
                  color: selectedCategory === category.id ? 'white' : 'text.primary',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 3
                  }
                }}
                onClick={() => setSelectedCategory(category.id)}
              >
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Box sx={{ mb: 1 }}>{category.icon}</Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {category.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* FAQ Accordion */}
        <Box>
          {filteredFaqs.map((faq, index) => (
            <Accordion
              key={index}
              expanded={expanded === `panel${index}`}
              onChange={handleChange(`panel${index}`)}
              sx={{ mb: 1 }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls={`panel${index}-content`}
                id={`panel${index}-header`}
              >
                <Typography sx={{ fontWeight: 600 }}>{faq.question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* CTA Section */}
        <Box sx={{ textAlign: 'center', mt: 8, p: 4, bgcolor: 'primary.main', color: 'white', borderRadius: 2 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
            Still have questions?
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            We're here to help! Start your free 14-day trial or get in touch with our team.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}
              onClick={() => navigate('/register')}
            >
              Start Free Trial
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{ borderColor: 'white', color: 'white', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
              onClick={() => window.location.href = 'mailto:support@taylindigital.com'}
            >
              Contact Support
            </Button>
          </Stack>
        </Box>
      </Container>
    </Box>
  )
}

export default FAQPage
